import classNames from "classnames";
import CiroButton from "../../../shared/CiroButton";
import { gql } from "@apollo/client";
import { AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatusFragment } from "../../../../__generated__/graphql";
import AutopilotSessionWebhookExportModalHeader from "./AutopilotSessionWebhookExportModalHeader";
import {
  CheckBadgeIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import pluralize from "pluralize";

export const AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatus = gql`
  fragment AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatus on ExportToWebhookTransactionStatusResponse {
    sentCount
    previouslyExportedAndSkippedCount
    errors {
      orgContact {
        id
        first_name
        last_name
      }
      message
      statusCode
    }
  }
`;

interface IAutopilotSessionWebhookExportModalSuccessProps {
  exportToWebhookTransactionStatus:
    | AutopilotSessionWebhookExportModalSuccess_ExportToWebhookTransactionStatusFragment
    | undefined;
  onClose: () => void;
}

const AutopilotSessionWebhookExportModalSuccess = ({
  exportToWebhookTransactionStatus,
  onClose,
}: IAutopilotSessionWebhookExportModalSuccessProps) => {
  const { sentCount, previouslyExportedAndSkippedCount } =
    exportToWebhookTransactionStatus ?? {};

  return (
    <>
      <AutopilotSessionWebhookExportModalHeader
        title="Export completed"
        onClose={onClose}
      />
      <div className={classNames("px-6", "py-4")}>
        {exportToWebhookTransactionStatus && (
          <div className={classNames("flex", "flex-col", "gap-4")}>
            {/* Success Section */}
            <div
              className={classNames(
                "border-l-4",
                "border-orange-500",
                "bg-gray-50",
                "p-4",
                "rounded-r-lg",
              )}
            >
              <div className={classNames("flex", "items-start", "gap-3")}>
                <CheckBadgeIcon
                  className={classNames(
                    "w-6",
                    "h-6",
                    "text-orange-500",
                    "flex-shrink-0",
                    "mt-0.5",
                  )}
                />
                <div className={classNames("flex-1")}>
                  <h3
                    className={classNames(
                      "text-base",
                      "font-semibold",
                      "text-gray-900",
                      "mb-1",
                    )}
                  >
                    Export successful
                  </h3>
                  <p className={classNames("text-sm", "text-gray-600", "mb-2")}>
                    Finished exporting{" "}
                    <span
                      className={classNames("font-medium", "text-orange-600")}
                    >
                      {sentCount}
                    </span>{" "}
                    {pluralize("contact", sentCount)} to your webhook.
                  </p>
                  {Boolean(previouslyExportedAndSkippedCount) &&
                    previouslyExportedAndSkippedCount &&
                    previouslyExportedAndSkippedCount > 0 && (
                      <p className={classNames("text-sm", "text-gray-500")}>
                        <span className={classNames("font-medium")}>
                          {previouslyExportedAndSkippedCount}
                        </span>{" "}
                        contacts were skipped (previously exported)
                      </p>
                    )}
                </div>
              </div>
            </div>

            {/* Error Section */}
            {exportToWebhookTransactionStatus.errors &&
              exportToWebhookTransactionStatus.errors.length > 0 && (
                <div
                  className={classNames(
                    "border-l-4",
                    "border-orange-400",
                    "bg-orange-50",
                    "p-4",
                    "rounded-r-lg",
                  )}
                >
                  <div className={classNames("flex", "items-start", "gap-3")}>
                    <ExclamationTriangleIcon
                      className={classNames(
                        "w-6",
                        "h-6",
                        "text-orange-500",
                        "flex-shrink-0",
                        "mt-0.5",
                      )}
                    />
                    <div className={classNames("flex-1")}>
                      <h4
                        className={classNames(
                          "text-base",
                          "font-semibold",
                          "text-gray-900",
                          "mb-3",
                        )}
                      >
                        Errors
                      </h4>
                      <div
                        className={classNames(
                          "bg-white",
                          "rounded-md",
                          "p-3",
                          "border",
                          "border-orange-200",
                          "max-h-48",
                          "overflow-y-auto",
                        )}
                      >
                        <ul
                          className={classNames(
                            "text-sm",
                            "text-gray-700",
                            "space-y-2",
                          )}
                        >
                          {exportToWebhookTransactionStatus.errors.map(
                            (error, index) => (
                              <li
                                key={index}
                                className={classNames(
                                  "flex",
                                  "items-start",
                                  "gap-2",
                                )}
                              >
                                <span
                                  className={classNames(
                                    "w-1.5",
                                    "h-1.5",
                                    "bg-orange-400",
                                    "rounded-full",
                                    "mt-2",
                                    "flex-shrink-0",
                                  )}
                                />
                                <span
                                  className={classNames(
                                    "break-words",
                                    "min-w-0",
                                  )}
                                >
                                  <span className={classNames("font-medium")}>
                                    {error.orgContact.first_name}{" "}
                                    {error.orgContact.last_name}
                                  </span>
                                  <span className={classNames("text-gray-600")}>
                                    : {error.message}
                                  </span>
                                </span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
          </div>
        )}
      </div>

      <div className={classNames("px-6", "py-4", "flex", "justify-end")}>
        <CiroButton
          analyticsField="Close Webhook Export Success"
          onClick={onClose}
        >
          Done
        </CiroButton>
      </div>
    </>
  );
};

export default AutopilotSessionWebhookExportModalSuccess;
